import { GetState, SetState } from "zustand";

import { FilterOptions, GridColumn, IAvailableSummarizeField, SummaryFunction } from "../../../../schemas/report";

import { formatSummarizeByAndAggregationOperations } from "../../utils";

import Config from "../../Config";
import { ReportStore } from "../model";
import { fetchEmployeeFields, fetchGoalReportCSV, fetchGroups } from "../requests";

const goalReportStore = (set: SetState<ReportStore>, get: GetState<ReportStore>) => ({
	fetchGoalReportCSV: async () => {
		const {
			client,
			report,
			getEnabledGridColumns,
			sorts,
			isSummarizeRowsView,
			enabledSummarizeFields,
			getSummaryColumnMetadata,
		} = get();

		if (!client) throw new Error("Client not initialized");
		if (!report) throw new Error("Report not initialized");

		const columns = getEnabledGridColumns().map((column) => {
			const type = column.fieldType;
			const columnID = column.field;

			return {
				type,
				id: columnID,
			};
		});

		const filters = get().getAppliedFilters();

		let aggregationOperations: {
			type: any;
			name: string;
			operation: SummaryFunction;
		}[] = [];
		let summarizeBy = [];
		let summaryColumnMetadata = {};
		let summarizeByMetadata = {};

		if (isSummarizeRowsView) {
			summaryColumnMetadata = getSummaryColumnMetadata();

			const { summarizeBy: formattedSummarizeBy, aggregationOperations: formattedAggregationOperations } =
				formatSummarizeByAndAggregationOperations(enabledSummarizeFields, summaryColumnMetadata);
			summarizeBy = formattedSummarizeBy;
			aggregationOperations = formattedAggregationOperations;

			summarizeByMetadata = enabledSummarizeFields.reduce((acc, field) => {
				acc[field.value] = {
					selection: field.selection,
				};
				return acc;
			}, {});
		}

		const csvJobID = await fetchGoalReportCSV(
			client,
			columns,
			filters,
			sorts,
			report.name,
			summarizeBy,
			aggregationOperations,
			isSummarizeRowsView,
			summaryColumnMetadata,
			summarizeByMetadata,
			report?.showAllAggregationColumns
		);

		set({ csvJobID });
	},
	fetchAvailableGoalColumns: async () => {
		const { client, columnMetadata, report } = get();

		if (!client) throw new Error("Client not initialized");

		const goalFields = Config.goalFields;
		const goalFieldsAsColumns: GridColumn[] = Object.keys(goalFields).map((field) => {
			return {
				field: `goal_${field}`,
				headerName: goalFields[field].headerName,
				sortable: true,
				pinned: null,
				fieldType: "GOAL_FIELD",
				fieldValueType: goalFields[field].type,
				fieldValueTypeLabel: Config.goalTypeLabels[goalFields[field].type],
				fieldPath: goalFields[field].path,
				columnMetadata: columnMetadata[field],
			};
		});

		const employeeFields = await fetchEmployeeFields(client, report);
		const employeeFieldsAsColumns: GridColumn[] = employeeFields.map((field) => {
			return {
				field: `employee_${field.name}`,
				headerName: field.name,
				sortable: true,
				pinned: null,
				fieldType: "EMPLOYEE_FIELD",
				fieldValueType: field.type,
				fieldPath: ["assignee", "custom_values", field.name],
				columnMetadata: columnMetadata[`employee_${field._id}`],
			};
		});

		const additionalEmployeeFieldsAsColumns: GridColumn[] = [
			{
				field: "employee_name",
				headerName: "Full Name",
				sortable: true,
				pinned: null,
				fieldType: "EMPLOYEE_FIELD",
				fieldValueType: "text",
				fieldPath: ["assignee", "name"],
				columnMetadata: columnMetadata["employee_name"],
			},
			{
				field: `employee_manager_email`,
				headerName: "Manager's Email",
				sortable: true,
				pinned: null,
				fieldType: "EMPLOYEE_FIELD",
				fieldValueType: "text",
				fieldPath: ["assignee", "manager", "email"],
				columnMetadata: columnMetadata[`employee_manager_email`],
			},
		];

		// combine and sort custom and additional employee fields
		const allGoalFieldsAsColumns = [
			...employeeFieldsAsColumns,
			...additionalEmployeeFieldsAsColumns,
			...goalFieldsAsColumns,
		].sort((a, b) => {
			if (a.headerName < b.headerName) {
				return -1;
			}
			if (a.headerName > b.headerName) {
				return 1;
			}
			return 0;
		});

		return allGoalFieldsAsColumns;
	},
	fetchGoalFilterOptions: async (): Promise<FilterOptions> => {
		const { client } = get();

		if (!client) throw new Error("Client not initialized");

		const groups = await fetchGroups(client);

		const department = groups.map((group) => {
			return {
				value: group._id,
				label: group.name,
			};
		});

		return {
			department,
		};
	},
	fetchAvailableGoalSummarizeFields: (): IAvailableSummarizeField[] => [
		...Config.summarizeFields.employee,
		...Config.summarizeFields.goal,
	],
});

export default goalReportStore;
